import React, { useCallback, useMemo, useState } from 'react';
import { create, InstanceProps } from 'react-modal-promise';
import { WMButton, WMButtonProps, WMButtonVariant, WMSearchFilter } from '@walkme/wm-ui';
import recipientLoadingFailed from '../../../../assets/images/recipient-loading-failed.png';
import StyledWMDialog from '../../Dialogs/StyledWMDialog/StyledWMDialog';
import AppTable from '../../AppTable/AppTable';
import Email from './Email';

import classes from './RecipientsListModal.module.scss';
import { useRecipientsList } from './recipientsList.hooks';
import { getFilterForTab, RecipientsListModalTabs, Tabs, useRecipientsModalTabs } from './RecipientsListModalTabs';
import CSVExportDialog from '../../NotificationsList/NotificationsDataExport/CSVExportDialog';
import { AudienceFragmentDoc, AudienceGraphql, ReportParamsNew, ReportTypes } from '../../../../generated/apollo-hooks';
import useEditAudience from '../AudiencesTable/hooks/rowActions/useEditAudience';
import { useApolloClient } from '@apollo/client';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';
import { ALL_IN_NOTIFICATION_ID, RecipientsModalTitle } from './RecipientsModalTitle';
import { useTranslation } from 'react-i18next';
import { CC_PERMISSIONS, PERM_TYPES } from '@communication-center/data-objects';
import { useHasRequiredPermissions } from '../../../infrastructure/Permissions/PermissionsProvider';
import { EmptyListImage } from '../LazyLoadImages/EmptyListImage';

const keyPrefix = 'components.audiences.recipientsListModal.recipientsListModal';

export const dataTestId = 'recipients-list-modal';

interface IRecipientsListModalProps extends InstanceProps<void> {
    title: string;
    id: string;
    tab?: Tabs;
    notificationName?: string;
    audiences?: { title: string; id: string }[];
    endUsersIds?: string[];
    disableEdit?: boolean;
}

const RecipientsListModal = ({
    isOpen,
    title,
    id: audienceId,
    onResolve,
    close,
    tab,
    notificationName,
    audiences,
    endUsersIds,
    disableEdit
}: IRecipientsListModalProps) => {
    const { t } = useTranslation('', { keyPrefix });
    const { activeTab, setActiveTab, getFilterForTab: tabFilter } = useRecipientsModalTabs(tab);
    const dialogTitle = title || t('recipients');
    const [selectedAudience, setSelectedAudience] = useState({ title: dialogTitle, audienceId });
    const audiencesIdsMemoized = useMemo(() => {
        if (audienceId === 'all') {
            return undefined;
        }
        if (selectedAudience.audienceId === ALL_IN_NOTIFICATION_ID) {
            return audiences?.map(it => it.id);
        }
        return [selectedAudience.audienceId];
    }, [audienceId, selectedAudience.audienceId, audiences]);

    const columns = useMemo(
        () => [
            {
                title: t('recipients'),
                key: 'email',
                dataKey: 'email',
                sortable: false,
                width: 250,
                minWidth: 60,
                flexGrow: 1,
                cellRenderer: ({ rowData }: $TSFixMe) => <Email rowData={rowData} />
            }
        ],
        [t]
    );

    const getSearchInputPlaceholder = useCallback(
        (activeTab: Tabs, audienceId: string) => {
            let tabString = {
                [Tabs.ALL]: t('tabName.all'),
                [Tabs.ACTIVE]: t('tabName.active'),
                [Tabs.INACTIVE]: t('tabName.inactive'),
                [Tabs.POTENTIAL]: t('tabName.potential')
            }[activeTab];
            return audienceId === 'all' ? t('searchPlaceholder1', { tabString }) : t('searchPlaceholder2', { tabString });
        },
        [t]
    );

    const {
        isLoading,
        isLoadingMore,
        recipientsList,
        error,
        fetchNextPage,
        onSearch,
        tableRef,
        clientSideFiltered,
        environmentId,
        retry,
        allRecipientsCount,
        getAllRecipientsSizeError,
        query,
        setQuery,
        recipientsCountResult
    } = useRecipientsList({
        audiencesIds: audiencesIdsMemoized,
        getFilter: tabFilter,
        endUsersIds: selectedAudience.audienceId === ALL_IN_NOTIFICATION_ID ? endUsersIds : undefined,
        isAllRecipients: audienceId === 'all'
    });

    const openRecipientsExportDialog = useCallback(async () => {
        close();
        const reportParams: ReportParamsNew = { filter: getFilterForTab(activeTab) };
        if (audienceId !== 'all') {
            if (selectedAudience.audienceId === ALL_IN_NOTIFICATION_ID) {
                reportParams.audiences = audiences!.map(it => ({ audienceName: it.title, audienceId: it.id }));
                if (endUsersIds?.length) {
                    reportParams.endUsersIds = endUsersIds;
                }
            } else {
                reportParams.audiences = [{ audienceName: selectedAudience.title, audienceId: selectedAudience.audienceId }];
            }
        }
        await CSVExportDialog.openModal({
            reportType: ReportTypes.RecipientsList,
            reportParams,
            title: t('modal'),
            withDateSelection: false
        });
    }, [activeTab, audienceId, audiences, close, endUsersIds, selectedAudience.audienceId, selectedAudience.title, t]);

    const editAudience = useEditAudience();
    const apolloClient = useApolloClient();

    const openEditAudienceModal = () => {
        close();

        const cacheAudience = apolloClient.readFragment<AudienceGraphql>({
            id: `AudienceGraphql:${selectedAudience.audienceId}`,
            fragment: AudienceFragmentDoc,
            fragmentName: 'Audience'
        });

        return editAudience(cacheAudience);
    };

    const dialogButtons: WMButtonProps[] = [
        {
            children: t('cancel'),
            variant: WMButtonVariant.Text,
            className: classes.cancelButton,
            onClick: () => onResolve(undefined),
            ['data-testid' as $TSFixMe]: `${dataTestId}-discard`
        },
        {
            children: t('export'),
            variant: WMButtonVariant.Primary,
            onClick: openRecipientsExportDialog,
            disabled: recipientsList.length === 0,
            type: 'button',
            ['data-testid' as $TSFixMe]: `${dataTestId}-confirm`
        }
    ];

    const hasAudienceEditPermissions = useHasRequiredPermissions([CC_PERMISSIONS.Audiences, PERM_TYPES.Write]);

    if (audienceId !== 'all' && !disableEdit && hasAudienceEditPermissions) {
        dialogButtons.unshift({
            children: t('editAudience'),
            iconComponent: <EditIcon />,
            disabled: selectedAudience.audienceId === ALL_IN_NOTIFICATION_ID,
            variant: WMButtonVariant.Text,
            onClick: openEditAudienceModal,
            type: 'button',
            ['data-testid' as $TSFixMe]: `${dataTestId}-editAudience`,
            className: classes.editAudienceBtn
        });
    }
    const totalAudiences = (audiences?.length ?? 0) + (endUsersIds?.length ? 1 : 0);

    return (
        <StyledWMDialog
            className={classes.dialogRoot}
            classes={{
                content: classes.dialogContent,
                title: classes.title,
                titleIcon: classes.titleIcons,
                closeButton: classes.closeButton
            }}
            title={selectedAudience.title}
            disableEnforceFocus
            titleIcon={
                totalAudiences > 1 ? (
                    <RecipientsModalTitle
                        title={title}
                        notificationName={notificationName!}
                        audiences={audiences}
                        setSelectedAudeince={setSelectedAudience}
                    />
                ) : undefined
            }
            open={isOpen}
            onClose={() => onResolve()}
            data-testid={dataTestId}
            buttons={dialogButtons}
            disableHeaderDivider
        >
            <div className={classes.dialogContent}>
                <RecipientsListModalTabs
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    getAllRecipientsSizeError={getAllRecipientsSizeError}
                    allRecipientsCount={allRecipientsCount}
                    setQuery={setQuery}
                    isLoading={isLoading}
                    query={query}
                />
                <div className={classes.searchContainer}>
                    <WMSearchFilter
                        onSearch={onSearch}
                        placeholder={getSearchInputPlaceholder(activeTab, audienceId)}
                        openByDefault
                        maxWidth="100%"
                        data-testid={`${dataTestId}-search`}
                        disabled={!(allRecipientsCount || recipientsList.length)}
                    />
                </div>
                <div className={classes.recipientsContainer}>
                    {error ? (
                        <div className={classes.errorContainer}>
                            <img src={recipientLoadingFailed} />
                            <div className={classes.errorText}>{t('loadingFailed')}</div>
                            <WMButton className={classes.retry} variant={WMButtonVariant.Text} onClick={retry}>
                                {t('retry')}
                            </WMButton>
                        </div>
                    ) : !recipientsCountResult && !isLoading && !clientSideFiltered.length ? (
                        <div className={classes.tableEmptyState} data-testid={`${dataTestId}-no-recipients-found`}>
                            <EmptyListImage />
                            <div className={classes.emptyStateText}>{t('noRecipients')}</div>
                        </div>
                    ) : (
                        <AppTable
                            hideToolbar
                            tableName="recipient-list"
                            classNames={{ tableContainer: classes.tableContainer }}
                            columns={columns}
                            data={isLoading ? (clientSideFiltered.length ? clientSideFiltered : undefined) : recipientsList}
                            environmentId={environmentId}
                            loadingRows={Array(10).fill({ id: null })}
                            data-testid={`${dataTestId}-table`}
                            wmTableProps={{
                                rowHeight: 40,
                                headerHeight: 40,
                                loadingMore: isLoadingMore || (isLoading && !!clientSideFiltered.length),
                                onEndReachedThreshold: 300,
                                onEndReached: fetchNextPage,
                                ref: tableRef,
                                disableCellPaddings: true
                            }}
                        />
                    )}
                </div>
            </div>
        </StyledWMDialog>
    );
};

RecipientsListModal.openModal = create(RecipientsListModal);

export default RecipientsListModal;
